// ISLES'24 scores beside their trivial controls. BLOOD-002 (research/2026-10-02-blood-spatial-result.md)
// scored v5's tissue model as the Stroke scene plays it (uniform allocation) against 42 ISLES'24
// patients' follow-up infarct, beside the two trivial predictions its protocol registered: no infarct
// and the admission core. scripts/isles-v5.ts copies those rows from its frozen evidence into
// isles-v5.json, which the app bundles. hemo's older comparison export (sims/hemo/isles24-*, which
// scored v4) carries the same admission core, so the app sets both beside the model the same way.

/** One prediction of one patient's infarct, mL, and how far off it was. NaN where a table has none. */
export interface IslesScore {
  ml: number;
  /** |predicted − measured| volume. */
  errMl: number;
  dice: number;
}

/** One patient: the measured infarct, the model's prediction and the two trivial ones. */
export interface IslesScored {
  measuredMl: number;
  model: IslesScore;
  zero: IslesScore;
  core: IslesScore;
}

export interface IslesV5Case extends IslesScored {
  case: string;
  /** hemo's historical assignment of the patient to a run: clot site, M1 part ('proximal'; '' for the
   *  one unclassified case), collateral grade and arm ('3h' or 'untreated'), and whether a right-sided
   *  clot was compared with the mirrored run. Model-clock assignments, not recorded treatment times. */
  site: string;
  part: string;
  grade: string;
  treatment: string;
  mirrored: boolean;
}

export interface IslesV5 {
  format: 'ba-app-isles-scores-v0';
  id: string;
  /** The model scored: the Stroke scene's `model` value. */
  scored: string;
  /** The evidence's names for the three predictions. */
  predictors: { model: string; zero: string; core: string };
  target: string;
  source: {
    result: string;
    evidence: string;
    commit: string;
    protocolRegisteredUtc: string;
    bindingSha256: string;
    summarySha256: string;
    scoredUtc: [string, string];
  };
  cases: IslesV5Case[];
}

/** A group's medians: the measured infarct, and each prediction's volume, error and Dice. */
export interface GroupScores {
  n: number;
  measuredMl: number;
  model: IslesScore;
  zero: IslesScore;
  core: IslesScore;
}

/** The median of the finite values; NaN for none. */
const median = (xs: number[]): number => {
  const v = xs.filter(Number.isFinite).sort((a, b) => a - b);
  if (!v.length) return NaN;
  const m = v.length >> 1;
  return v.length % 2 ? v[m]! : (v[m - 1]! + v[m]!) / 2;
};

export function groupScores(cases: readonly IslesScored[]): GroupScores {
  const of = (k: 'model' | 'zero' | 'core'): IslesScore => ({
    ml: median(cases.map((c) => c[k].ml)),
    errMl: median(cases.map((c) => c[k].errMl)),
    dice: median(cases.map((c) => c[k].dice)),
  });
  return { n: cases.length, measuredMl: median(cases.map((c) => c.measuredMl)), model: of('model'), zero: of('zero'), core: of('core') };
}

/** Predicting no infarct: off by the whole infarct, no overlap (Dice 1 where there is none to find,
 *  as BLOOD-002 scores it). */
export const noInfarct = (measuredMl: number): IslesScore => ({ ml: 0, errMl: measuredMl, dice: measuredMl === 0 ? 1 : 0 });

/** The registered group for the run on screen: v5's proximal left M1 clot, untreated or reperfused at
 *  3 h, every collateral grade (v5's grades are one run). Null for any other run: BLOOD-002 scored
 *  only these. */
export function v5Group(data: IslesV5, choices: { model?: string; site?: string; part?: string; treatment?: string }): { arm: string; cases: IslesV5Case[] } | null {
  if (choices.model !== data.scored || choices.site !== 'l-m1' || (choices.part ?? '') !== '') return null;
  const arm = (choices.treatment ?? '').replace(/^reperf-/, '');
  if (arm !== '3h' && arm !== 'untreated') return null;
  const cases = data.cases.filter((c) => c.site === 'l-m1' && c.treatment === arm);
  return cases.length ? { arm, cases } : null;
}
